import React, { useEffect, useRef, useState } from 'react';
import { API_URL } from './api';

function BusinessResult({ action, data }) {
  const rows = action === 'get_recent_orders' ? data?.orders : action === 'get_reservations' ? data?.reservations : null;
  if (!Array.isArray(rows) || !rows.length) return null;
  return <ul>{rows.slice(0, 20).map((row, index) => <li key={row.id || index}>{action === 'get_recent_orders'
    ? `Ticket ${row.ticketNumber || row.id} · ${row.status || 'État inconnu'} · ${Number(row.totalTtc || 0).toFixed(2)} €`
    : `${row.reservationTime || row.time || ''} · ${row.partySize || 0} personne(s) · ${row.status || 'État inconnu'}`}</li>)}</ul>;
}

export default function AiAssistantWidget({ request, scopeKey, onActionDone }) {
  const [isOpen, setIsOpen] = useState(false);
  const [capabilities, setCapabilities] = useState(null);
  const [capabilityError, setCapabilityError] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmingId, setConfirmingId] = useState('');
  const messagesEndRef = useRef(null);
  const generation = useRef(0);
  const sending = useRef(false);
  const confirming = useRef(false);

  useEffect(() => {
    generation.current += 1;
    setConversationId(null); setMessages([]); setDraft(''); setCapabilities(null);
    setLoading(false); setConfirmingId(''); setCapabilityError('');
    sending.current = false; confirming.current = false;
    return () => { generation.current += 1; };
  }, [scopeKey]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let active = true;
    setCapabilities(null); setCapabilityError('');
    request(`${API_URL}/api/ai-assistant/capabilities`)
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !Array.isArray(payload.actions) || !Array.isArray(payload.modules)) throw Error(payload.error || 'Applications accessibles indisponibles.');
        if (active) setCapabilities(payload);
      })
      .catch((error) => { if (active) setCapabilityError(error.message); });
    return () => { active = false; };
  }, [isOpen, scopeKey, request]);

  useEffect(() => { if (isOpen) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [isOpen, messages]);
  const pushMessage = (message) => setMessages((current) => [...current, { id: `${Date.now()}-${Math.random()}`, ...message }]);

  async function sendMessage(event) {
    event?.preventDefault?.();
    const text = draft.trim();
    if (!text || sending.current || !capabilities?.actions?.length) return;
    const version = generation.current;
    sending.current = true; setLoading(true); setDraft('');
    pushMessage({ role: 'user', text });
    try {
      const response = await request(`${API_URL}/api/ai-assistant/message`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: text, conversationId }) });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw Error(payload.error || 'Assistant indisponible.');
      if (version !== generation.current) return;
      setConversationId(payload.conversationId || conversationId);
      const allowed = capabilities.actions.includes(payload.action);
      pushMessage({ role: 'assistant', text: payload.message || 'Demande traitée.', action: payload.action, data: allowed ? payload.data : null,
        confirmation: payload.type === 'confirmation_required' && allowed && payload.confirmationId ? { id: payload.confirmationId, action: payload.action, expiresAt: payload.expiresAt } : null });
    } catch (error) {
      if (version !== generation.current) return;
      setDraft(text); pushMessage({ role: 'assistant', tone: 'error', text: error.message });
    } finally {
      if (version === generation.current) { sending.current = false; setLoading(false); }
    }
  }

  async function resolveConfirmation(confirmation, confirmed) {
    if (confirming.current || !capabilities?.actions?.includes(confirmation.action)) return;
    const version = generation.current;
    confirming.current = true; setConfirmingId(String(confirmation.id));
    try {
      const response = await request(`${API_URL}/api/ai-assistant/confirm`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ confirmationId: confirmation.id, confirmed }) });
      const payload = await response.json().catch(() => ({}));
      if (version !== generation.current) return;
      if (response.ok || [401, 403, 404, 409, 410].includes(response.status)) {
        setMessages((current) => current.map((message) => message.confirmation?.id === confirmation.id ? { ...message, confirmation: null } : message));
      }
      if (!response.ok) throw Error(payload.error || 'Confirmation impossible.');
      pushMessage({ role: 'assistant', text: payload.message || (confirmed ? 'Action traitée.' : 'Action annulée.') });
      if (confirmed && payload.type === 'done') await onActionDone?.(payload);
    } catch (error) {
      if (version === generation.current) pushMessage({ role: 'assistant', tone: 'error', text: error.message });
    } finally {
      if (version === generation.current) { confirming.current = false; setConfirmingId(''); }
    }
  }

  return <>
    <button type="button" className="ai-assistant-launcher" onClick={() => setIsOpen((current) => !current)} aria-expanded={isOpen} aria-controls="ai-assistant-panel">Assistant IA</button>
    {isOpen ? <section id="ai-assistant-panel" className="ai-assistant-panel" aria-label="Assistant du restaurateur">
      <div className="ai-assistant-header"><div><span>Assistant IA</span><strong>Votre restaurant</strong></div><button type="button" onClick={() => setIsOpen(false)} aria-label="Fermer l’assistant">×</button></div>
      <div className="ai-assistant-messages" role="log" aria-live="polite" aria-relevant="additions">
        <div className="ai-assistant-empty">
          <strong>Vos applications</strong>
          {capabilityError ? <p role="alert">{capabilityError}</p> : !capabilities ? <p>Vérification de vos accès…</p> : <>
            <p>{capabilities.modules.length ? capabilities.modules.map((module) => `${module.label}${module.ready === false ? ' (connexion à configurer)' : ''}`).join(' · ') : 'Aucune application accessible.'}</p>
            <p>Préparez vos actions depuis cet assistant. Les modifications et impressions demandent votre confirmation ; vos droits sont revérifiés avant chaque action.</p>
            {!messages.length ? <div className="ai-assistant-confirmation">{(capabilities.examples || []).map((example) => <button key={example} type="button" className="admin-button admin-button-secondary" onClick={() => setDraft(example)}>{example}</button>)}</div> : null}
          </>}
        </div>
        {messages.map((message) => <div key={message.id} className={`ai-assistant-message is-${message.role}${message.tone === 'error' ? ' is-error' : ''}`}>
          <p>{message.text}</p><BusinessResult action={message.action} data={message.data} />
          {message.confirmation && capabilities?.actions?.includes(message.confirmation.action) ? <div className="ai-assistant-confirmation">
            <button type="button" className="admin-button" disabled={!!confirmingId} onClick={() => resolveConfirmation(message.confirmation, true)}>Confirmer</button>
            <button type="button" className="admin-button admin-button-secondary" disabled={!!confirmingId} onClick={() => resolveConfirmation(message.confirmation, false)}>Annuler</button>
          </div> : null}
        </div>)}
        {loading ? <div className="ai-assistant-message is-assistant"><p>Traitement…</p></div> : null}<div ref={messagesEndRef} />
      </div>
      <form className="ai-assistant-input" onSubmit={sendMessage}>
        <textarea aria-label="Votre demande à l’assistant restaurant" maxLength={4000} value={draft} disabled={!capabilities?.actions?.length} onChange={(event) => setDraft(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); sendMessage(event); } }} placeholder="Demander à l’assistant" rows={2} />
        <button type="submit" className="admin-button" disabled={!draft.trim() || loading || !capabilities?.actions?.length}>Envoyer</button>
      </form>
    </section> : null}
  </>;
}
